/**
 * AdvancedBlock — Contenedor colapsable para campos/opciones avanzadas.
 *
 * Comportamiento según `useUIMode()`:
 *  - Modo Simple: contenido oculto por defecto tras un toggle
 *    "🔧 Mostrar opciones avanzadas" (recuerda estado en localStorage por `id`).
 *  - Modo Avanzado: contenido siempre visible; sin toggle. El usuario avanzado
 *    ve todo sin fricción.
 *
 * Props:
 *  - id: string único (persiste toggle en localStorage `mg_advblock_{id}`).
 *  - title: string, texto del toggle (default "Opciones avanzadas").
 *  - description: string opcional para tooltip/subtitle.
 *  - children: React.ReactNode a mostrar cuando esté abierto.
 *  - defaultOpen: bool, si Simple abre por defecto. Default false.
 *  - className: string opcional para el contenedor.
 */
import { useEffect, useState } from "react";
import { ChevronDown, Wrench } from "lucide-react";
import { useUIMode } from "../../hooks/useUIMode";
import { logUXEvent } from "../../lib/uxTelemetry";

function loadState(id, defaultOpen) {
  if (!id) return defaultOpen;
  try {
    const raw = localStorage.getItem(`mg_advblock_${id}`);
    if (raw === null) return defaultOpen;
    return raw === "1";
  } catch { return defaultOpen; }
}

function saveState(id, isOpen) {
  if (!id) return;
  try { localStorage.setItem(`mg_advblock_${id}`, isOpen ? "1" : "0"); } catch { /* storage full or blocked */ }
}

export function AdvancedBlock({
  id,
  title = "Opciones avanzadas",
  description,
  children,
  defaultOpen = false,
  className = "",
}) {
  const { isSimple } = useUIMode();
  const [open, setOpen] = useState(() => loadState(id, defaultOpen));

  useEffect(() => { saveState(id, open); }, [id, open]);

  // Modo avanzado → siempre visible, sin chrome de toggle
  if (!isSimple) {
    return (
      <div
        className={`space-y-3 ${className}`}
        data-testid={id ? `advblock-${id}-content` : undefined}
      >
        {children}
      </div>
    );
  }

  // Modo simple → toggle
  const handleToggle = () => {
    setOpen((o) => {
      const next = !o;
      // Telemetría (fire-and-forget) — solo cuando el usuario interactúa,
      // no en el load-from-localStorage inicial.
      if (id) {
        logUXEvent("advblock_toggle", { block_id: id, open: next });
      }
      return next;
    });
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={open}
        className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors py-1"
        data-testid={id ? `advblock-${id}-toggle` : "advblock-toggle"}
      >
        <Wrench className="w-3.5 h-3.5" />
        <span>{title}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
        {description && !open && (
          <span className="hidden md:inline text-[11px] text-muted-foreground/70">
            · {description}
          </span>
        )}
      </button>
      {open && (
        <div
          className="pl-5 border-l-2 border-primary/20 space-y-3"
          data-testid={id ? `advblock-${id}-content` : undefined}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * SimpleOnly — Wrapper opuesto: solo renderiza en modo Simple.
 * Útil para mostrar CTAs/hints que solo tienen sentido en Simple.
 */
export function SimpleOnly({ children }) {
  const { isSimple } = useUIMode();
  return isSimple ? children : null;
}

/**
 * AdvancedOnly — Wrapper: solo renderiza en modo Avanzado.
 * Útil para columnas extra en tablas, filtros power-user, etc.
 */
export function AdvancedOnly({ children }) {
  const { isAdvanced } = useUIMode();
  return isAdvanced ? children : null;
}
